import { useState } from "react";
import type { Lang } from "../lib/i18n";
import { pick } from "../lib/i18n";
import { apiRequest } from "../lib/api";

type Status = "idle" | "sending" | "sent" | "error";

/** "Send me the link later" modal (kD) — emails the portal link via /api/send-link. */
export function SendLinkModal({ lang, onClose }: { lang: Lang; onClose: () => void }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/)) {
      setStatus("error");
      setError(t("يرجى إدخال بريد إلكتروني صحيح", "Please enter a valid email"));
      return;
    }
    setStatus("sending");
    setError("");
    try {
      // Backend expects `email`, not `to` — using the wrong key made every
      // request fail with "missing email" (400), surfaced to the user as a
      // generic "failed to send" message.
      const res = await apiRequest("/api/send-link", { method: "POST", body: JSON.stringify({ email, lang }) });
      if (res.ok && (res.data as any)?.ok !== false) {
        setStatus("sent");
      } else {
        setStatus("error");
        const code = res.error || "";
        setError(
          code === "no-api"
            ? t("خدمة البريد غير متصلة بالخادم بعد. يُرجى التواصل مع قسم تقنية المعلومات.", "The mail service is not yet connected to the server. Please contact IT.")
            : code === "SMTP not configured"
              ? t("لم يتم ضبط بيانات SMTP في الخادم. يُرجى التواصل مع قسم تقنية المعلومات.", "SMTP credentials are not configured on the server. Please contact IT.")
              : t("تعذّر إرسال البريد. يُرجى المحاولة لاحقاً.", "Failed to send email. Please try again later."),
        );
      }
    } catch {
      setStatus("error");
      setError(t("حدث خطأ غير متوقع", "An unexpected error occurred"));
    }
  }

  return (
    <div className="kd-admin-modal-backdrop" onClick={onClose}>
      <div className="kd-send-link-modal" onClick={(e) => e.stopPropagation()} dir={lang === "ar" ? "rtl" : "ltr"}>
        <div className="kd-admin-modal-header">
          <h2>{t("أرسل لي الرابط لاحقاً", "Send me the link later")}</h2>
          <button className="kd-admin-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="kd-admin-modal-body">
          {status === "sent" ? (
            <div className="kd-send-link-success">
              <div className="kd-send-link-success-icon">✉️</div>
              <h3>{t("تم الإرسال!", "Sent!")}</h3>
              <p>
                {t(
                  `تحقّق من بريدك (${email}) وافتح الرابط من جهاز الكمبيوتر لإكمال الرحلة بأفضل تجربة.`,
                  `Check your inbox (${email}) and open the link from your computer for the best experience.`,
                )}
              </p>
              <button className="kd-btn-primary kd-btn-gold" onClick={onClose}>{t("إغلاق", "Close")}</button>
            </div>
          ) : (
            <form onSubmit={submit} className="kd-send-link-form">
              <p className="kd-send-link-intro">
                {t(
                  "أدخل بريدك الإلكتروني وسنرسل لك رابط البوابة لإكمال الرحلة من جهاز الكمبيوتر متى أردت.",
                  "Enter your email and we will send you the portal link to complete the journey from a computer whenever you want.",
                )}
              </p>
              <label>
                <span>{t("البريد الإلكتروني", "Email address")}</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@kaden-sa.com" required autoFocus />
              </label>
              {error && <div className="kd-send-link-error">{error}</div>}
              <button type="submit" className="kd-btn-primary kd-btn-gold" disabled={status === "sending"}>
                {status === "sending" ? <span className="kd-spinner" /> : t("إرسال الرابط", "Send link")}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
